import { ScrollableArea } from "@nova/component-scrollable-area"
import { getGenericListItemWrapper } from "../generics/GenericListItemWrapper"
import { DragAndDroppableListBuffer } from "../dragAndDrop"
import { SidePanelListEmptyState } from "../SidePanelListEmptyState"
import { GroupType } from "@nova/constants"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useState } from "react"
import { useCreateGroup } from "../generics/genericMultipleSelectedContextMenuActions"
import { clearSelection, selectSelectedLayerIds, setRenamingLayerId } from "../slice.new"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import * as Icons from "@nova/component-icons"
import { useFlattenedFilteredAndSortedPdfSidePanelData } from "../useSidePanelPdfs"
import { useVirtualizedList } from "tools/hooks/useVirtualizedList"
import { createPortal } from "react-dom"
import { dataSelectorsByMode } from "../constants"

const ListItemWrapper = getGenericListItemWrapper({
    mode: "PDFs",
})

export function SidePanelPDFsList() {
    const dispatch = useAppDispatch()
    const [hoveredId, setHoveredId] = useState<string | undefined>(undefined)

    const flattenedPdfs = useFlattenedFilteredAndSortedPdfSidePanelData()
    const { parentRef: pdfParentRef, virtualizer: pdfVirtualizer } =
        useVirtualizedList(flattenedPdfs)

    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelList"))
    const onContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            dispatch(
                setActiveContextMenu({
                    type: "SidePanelList",
                    data: {
                        mode: "PDFs",
                    },
                    position: { left: e.clientX, top: e.clientY },
                })
            )
        },
        [dispatch]
    )
    useEffect(() => {
        if (typeof window === "undefined") return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            dispatch(clearSelection())
        }
        window.addEventListener("keydown", handleEscape)
        return () => window.removeEventListener("keydown", handleEscape)
    }, [dispatch])
    const { handleCreateEmptyGroup } = useCreateGroup({
        mode: "PDFs",
        groupType: GroupType.PDF,
    })
    const sortedEntities = dataSelectorsByMode["PDFs"].useSortedEntities()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    return (
        <div
            className="flex-1 min-h-0 flex flex-col"
            onContextMenu={onContextMenu}
            onClick={() => {
                dispatch(clearSelection())
                dispatch(setActiveContextMenu(undefined))
                dispatch(setRenamingLayerId({ id: undefined }))
            }}
        >
            <ScrollableArea
                ref={pdfParentRef}
                className="shrink px-3 min-h-[40px]"
                shouldPreventScrollbarShifting
            >
                {!!flattenedPdfs.length ? (
                    <>
                        <DragAndDroppableListBuffer />
                        <div
                            style={{
                                height: `${pdfVirtualizer.getTotalSize()}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {pdfVirtualizer.getVirtualItems().map((virtualRow) => {
                                const item = flattenedPdfs[virtualRow.index]
                                return (
                                    <div
                                        key={item.id}
                                        data-index={virtualRow.index}
                                        ref={pdfVirtualizer.measureElement}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            transform: `translateY(${virtualRow.start}px)`,
                                            zIndex: selectedLayerIds[0] === item.id ? 2 : 1,
                                        }}
                                        onMouseEnter={() => setHoveredId(item.id)}
                                        onMouseLeave={() =>
                                            hoveredId === item.id && setHoveredId(undefined)
                                        }
                                    >
                                        <ListItemWrapper
                                            item={item}
                                            hoveredItemId={hoveredId}
                                            sortedEntities={sortedEntities}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                        <DragAndDroppableListBuffer />
                    </>
                ) : (
                    <SidePanelListEmptyState />
                )}
            </ScrollableArea>
            {activeSidePanelContextMenu?.data.mode === "PDFs" &&
                createPortal(
                    <ContextMenu
                        sections={[
                            [
                                {
                                    title: "New Group",
                                    Icon: Icons.Folder,
                                    onClick: handleCreateEmptyGroup,
                                },
                            ],
                        ]}
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        isFixedPosition={true}
                        top={activeSidePanelContextMenu.position.top}
                        left={activeSidePanelContextMenu.position.left}
                    />,
                    document.getElementById("portal")!
                )}
        </div>
    )
}
